/* eslint-disable no-await-in-loop */
import React, { useState } from 'react'
import { Document, pdf } from '@react-pdf/renderer'
import { PDFDocument } from 'pdf-lib'
import { CompletionPdf } from '@src/modules/Sites/router/screens/ViewSite/components/CompletionPdf'
import { AxiosResponse } from 'axios'

import { Completion, TemplateReportDailyData } from '@src/generatedClient/generated/taskreportsApi'

type Status = 'idle' | 'generating' | 'combining' | 'complete' | 'error'

type DataArg = {
  date: string
  data: AxiosResponse<TemplateReportDailyData>
}

interface CompletionReportGeneratorProps {
  fetchFunction: (arg: string) => Promise<DataArg>
  arguments: string[]
}

export function CompletionReportPDFGenerator({
  fetchFunction,
  arguments: args,
}: CompletionReportGeneratorProps): JSX.Element {
  const [status, setStatus] = useState<Status>('idle')
  const [progress, setProgress] = useState<number>(0)

  const generatePDFForData = async (
    completions: Completion[],
  ): Promise<ArrayBuffer> => {
    function PageDocument() {
      return (
        <Document>
          <CompletionPdf completions={completions} />
        </Document>
      )
    }

    try {
      const blob = await pdf(<PageDocument />).toBlob()
      console.log('Generated PDF blob size:', blob.size)
      return blob.arrayBuffer()
    } catch (error) {
      console.error('Error generating individual PDF:', error)
      throw error
    }
  }

  const generateAndCombinePDFs = async (): Promise<void> => {
    try {
      setStatus('generating')
      const pdfDocs: ArrayBuffer[] = []

      const completions: Completion[] = []
      for (let i = 0; i < args.length; i++) {
        console.log(`Processing document ${i + 1} of ${args.length}`)
        const pageData = await fetchFunction(args[i])
        if (pageData.data?.data?.completions?.length > 0) {
          completions.push(...pageData.data.data.completions)
        }
        setProgress(Math.round(((i + 1) / args.length) * 100))
      }
      
      const arrayBuffer = await generatePDFForData(
        completions
      )
      console.log(`Generated PDF size:`, arrayBuffer.byteLength)
      pdfDocs.push(arrayBuffer)

      setStatus('combining')
      console.log('Starting PDF combination')

      const mergedPdf = await PDFDocument.create()

      for (const pdfBytes of pdfDocs) {
        const p = await PDFDocument.load(pdfBytes)
        console.log('Loaded PDF page count:', p.getPageCount())
        const copiedPages = await mergedPdf.copyPages(p, p.getPageIndices())
        copiedPages.forEach((page) => mergedPdf.addPage(page))
      }

      const mergedPdfFile = await mergedPdf.save()
      console.log('Final merged PDF size:', mergedPdfFile.byteLength)

      const blob = new Blob([mergedPdfFile], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = 'combined.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      setStatus('complete')
    } catch (error) {
      console.error('Error in PDF generation process:', error)
      setStatus('error')
    }
  }

  return (
    <div className="py-4">
      <button
        onClick={generateAndCombinePDFs}
        type="button"
        disabled={status === 'generating' || status === 'combining'}
        className="px-4 py-2 text-white bg-blue-500 rounded disabled:bg-gray-300"
      >
        {status === 'idle' && 'Completion PDF'}
        {status === 'generating' && `Generating PDFs (${progress}%)`}
        {status === 'combining' && 'Combining PDFs...'}
        {status === 'complete' && 'Generation Complete'}
        {status === 'error' && 'Error Generating PDFs'}
      </button>

      {status !== 'idle' && (
        <div className="mt-4">
          <div className="h-2 bg-gray-200 rounded">
            <div
              className="h-full transition-all duration-300 bg-blue-500 rounded"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
